<script setup lang="ts">
import { nextTick, ref } from "vue";
import { novexDetModels, type NovexDetModel } from "@/data/novexDetModels";

withDefaults(defineProps<{ inDialog?: boolean; priority?: boolean }>(), {
  inDialog: false,
  priority: false,
});

const selectedModel = ref<NovexDetModel | null>(null);
const detailPanel = ref<HTMLElement | null>(null);

async function selectModel(model: NovexDetModel) {
  selectedModel.value = model;
  await nextTick();
  detailPanel.value?.scrollIntoView({
    block: "nearest",
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? "instant"
      : "smooth",
  });
}
</script>

<template>
  <div class="novex-model-explorer">
    <div class="novex-artwork">
      <button
        v-if="!inDialog"
        type="button"
        class="novex-artwork-photo"
        data-detail="novex-det"
        data-title="NOVEX DET"
        aria-label="Explore the NOVEX DET product family"
      >
        <img
          src="/brand/novex-det-2026.webp"
          width="1536"
          height="1024"
          loading="lazy"
          alt="NOVEX DET non-electric detonator wires and connectors at an open-pit mine, with the current NITRO-DETEX logo"
        />
      </button>
      <div v-else class="novex-artwork-photo">
        <img
          src="/brand/novex-det-2026.png"
          width="1536"
          height="1024"
          :loading="priority ? 'eager' : 'lazy'"
          alt="NOVEX DET non-electric detonator wires and connectors at an open-pit mine, with the current NITRO-DETEX logo"
        />
      </div>
      <div class="novex-model-buttons" role="group" aria-label="NOVEX DET models">
        <button
          v-for="model in novexDetModels"
          :key="model.code"
          type="button"
          :aria-label="`View NOVEX DET ${model.code} details`"
          :aria-pressed="selectedModel?.code === model.code"
          @click="selectModel(model)"
        >
          <span class="novex-model-wordmark" aria-hidden="true">NOVE<span>X</span> DET</span>
          <strong aria-hidden="true">{{ model.code }}</strong>
        </button>
      </div>
    </div>

    <p v-if="!selectedModel" class="novex-model-hint">
      Select LP, MS, SD or SDC above to view model details.
    </p>
    <section v-else ref="detailPanel" class="novex-model-detail" aria-live="polite">
      <p class="novex-model-detail-kicker">NON-ELECTRIC INITIATION / MODEL {{ selectedModel.code }}</p>
      <h4>NOVEX DET {{ selectedModel.code }}</h4>
      <p v-if="selectedModel.subtitle" class="novex-model-subtitle">{{ selectedModel.subtitle }}</p>
      <p v-if="selectedModel.description">{{ selectedModel.description }}</p>
      <p v-else>
        Part of the NOVEX DET non-electric detonator range. Model-specific product details
        will be added when supplied by the client.
      </p>
      <ul v-if="selectedModel.benefits?.length" class="novex-model-benefits">
        <li v-for="benefit in selectedModel.benefits" :key="benefit">{{ benefit }}</li>
      </ul>
      <details v-if="selectedModel.specifications?.length" class="novex-model-specifications">
        <summary>View LP technical specifications <span aria-hidden="true">+</span></summary>
        <div v-for="group in selectedModel.specifications" :key="group.title" class="novex-spec-group">
          <h5>{{ group.title }}</h5>
          <dl>
            <div v-for="[label, value] in group.entries" :key="label">
              <dt>{{ label }}</dt><dd>{{ value }}</dd>
            </div>
          </dl>
        </div>
        <p class="novex-spec-note">Confirm delay options and final product configuration with our team.</p>
      </details>
      <div class="novex-model-links">
        <a v-if="selectedModel.technicalSheet" :href="selectedModel.technicalSheet" target="_blank" rel="noopener">
          View LP technical sheet <span aria-hidden="true">↗</span>
        </a>
        <RouterLink :to="{ path: '/contact', query: { product: `NOVEX DET ${selectedModel.code}` } }">
          Enquire about this model <span aria-hidden="true">↗</span>
        </RouterLink>
      </div>
    </section>
  </div>
</template>
